import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useWeb3 } from '../../context/Web3Context';
import { useCreateBet } from '../../hooks/useContract';
import { createBet as createBetApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Slider } from '../ui/slider';
import { Alert, AlertDescription } from '../ui/alert';
import { CreateBetSummary } from './CreateBetSummary';
import { toast } from 'sonner';
import { 
    CalendarIcon, 
    TrendingUp, 
    Coins,
    AlertTriangle,
    Check,
    Loader2,
    ExternalLink,
    Wallet
} from 'lucide-react';
import { format, addDays, addHours } from 'date-fns';
import { cn } from '../../lib/utils';

const categories = [
    { value: 'sports', label: 'Sports' },
    { value: 'crypto', label: 'Crypto' },
    { value: 'politics', label: 'Politics' },
    { value: 'entertainment', label: 'Entertainment' },
    { value: 'custom', label: 'Custom' },
];

export function CreateBetForm() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { address, isConnected, connect, balance, chainId } = useWeb3();
    const { createBet, hash, isPending, isConfirming, isSuccess, error } = useCreateBet();
    
    const [formData, setFormData] = useState({
        description: '',
        category: 'custom',
        stake: '',
        oddsBps: 18000,
        resolutionDate: addDays(new Date(), 7),
        currency: 'ETH',
    });
    const [currencies, setCurrencies] = useState([]);

    const selectedCurrency = currencies.find(c => c.code === formData.currency) || { code: 'ETH', label: 'Ethereum', platform_fee_pct: 3.0, reward_multiplier: 1.0, badge: '', color: '#627EEA' };

    useEffect(() => {
        const API = process.env.REACT_APP_BACKEND_URL;
        fetch(`${API}/api/currencies`).then(r => r.json()).then(d => setCurrencies(d.currencies || [])).catch(() => {});
    }, []);

    const oddsDisplay = (formData.oddsBps / 10000).toFixed(2);
    const takerStake = formData.stake ? (parseFloat(formData.stake) * 10000 / (formData.oddsBps - 10000)).toFixed(4) : '0';
    const totalPool = formData.stake ? (parseFloat(formData.stake) + parseFloat(takerStake)).toFixed(4) : '0';
    const hasEnoughBalance = balance >= parseFloat(formData.stake || 0);
    const isCorrectChain = chainId === 8453; // Base Mainnet

    // Handle successful transaction
    useEffect(() => {
        if (isSuccess && hash) {
            toast.success('Bet created on blockchain!', {
                description: (
                    <div className="flex items-center gap-2">
                        <span>Transaction confirmed!</span>
                        <a 
                            href={`https://basescan.org/tx/${hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#00FF66] hover:underline flex items-center gap-1"
                        >
                            View <ExternalLink className="w-3 h-3" />
                        </a>
                    </div>
                ),
            });
            
            // Also save to backend for indexing
            saveToBackend();
        }
    }, [isSuccess, hash]);

    // Handle errors
    useEffect(() => {
        if (error) {
            console.error('Contract error:', error);
            toast.error('Transaction failed', {
                description: error.message?.includes('User rejected') 
                    ? 'You rejected the transaction'
                    : error.shortMessage || error.message || 'Please try again',
            });
        }
    }, [error]);

    const saveToBackend = async () => {
        try {
            const bet = await createBetApi({
                creator_address: address,
                description: formData.description,
                category: formData.category,
                creator_stake: parseFloat(formData.stake),
                odds_bps: formData.oddsBps,
                resolution_time: formData.resolutionDate.toISOString(),
                tx_hash_create: hash,
                on_chain: true,
                currency: formData.currency,
            });
            navigate(`/bet/${bet.id}`);
        } catch (err) {
            console.error('Failed to save to backend:', err);
            // Still show success since blockchain tx worked
            navigate('/bets');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!isConnected) {
            await connect();
            return;
        }

        if (!isCorrectChain) {
            toast.error('Wrong network', {
                description: 'Please switch to Base network',
            });
            return;
        }

        if (!formData.description || formData.description.length < 10) {
            toast.error('Description must be at least 10 characters');
            return;
        }

        if (!formData.stake || parseFloat(formData.stake) <= 0) {
            toast.error('Please enter a valid stake amount');
            return;
        }

        if (!hasEnoughBalance) {
            toast.error('Insufficient balance', {
                description: `You need ${formData.stake} ETH but only have ${balance.toFixed(4)} ETH`,
            });
            return;
        }

        try {
            await createBet(formData.description, oddsDisplay, formData.stake);
        } catch (err) {
            console.error('Create bet error:', err);
        }
    };

    const getButtonContent = () => {
        if (!isConnected) {
            return (
                <>
                    <Wallet className="w-5 h-5 mr-2" />
                    Connect Wallet
                </>
            );
        }
        if (!isCorrectChain) {
            return (
                <>
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Switch to Base
                </>
            );
        }
        if (isPending) {
            return (
                <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Confirm in Wallet...
                </>
            );
        }
        if (isConfirming) {
            return (
                <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Confirming Transaction...
                </>
            );
        }
        return (
            <>
                <Check className="w-5 h-5 mr-2" />
                Create Bet (Pay {formData.stake || '0'} ETH)
            </>
        );
    };

    return (
        <form onSubmit={handleSubmit} data-testid="create-bet-form">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Form */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Blockchain / Off-chain Notice */}
                    <Alert className="border-[#00FF66]/30 bg-[#00FF66]/10">
                        <Coins className="w-4 h-4 text-[#00FF66]" />
                        <AlertDescription className="text-white/80">
                            {formData.currency === 'ETH' ? (
                                <>
                                    <strong className="text-[#00FF66]">{t('createBet.realEthTitle')}</strong>{' '}
                                    {t('createBet.realEthDesc')}
                                </>
                            ) : (
                                <>
                                    <strong className="text-[#FFD800]">{t('createBet.offChainTitle', { currency: formData.currency })}</strong>{' '}
                                    {formData.currency === 'B4P' ? t('createBet.offChainB4p') : t('createBet.offChainUsdc')}
                                </>
                            )}
                        </AlertDescription>
                    </Alert>

                    {/* Bet Description */}
                    <Card className="bg-white/[0.02] border-white/5">
                        <CardHeader>
                            <CardTitle className="text-xl text-white flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-[#00FF66]/20 flex items-center justify-center">
                                    <Coins className="w-4 h-4 text-[#00FF66]" />
                                </div>
                                Bet Details
                            </CardTitle>
                            <CardDescription className="text-white/50">
                                Describe what you're betting on. Be specific and clear.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="description" className="text-white/80">Description</Label>
                                <Textarea
                                    id="description"
                                    placeholder="e.g., PSG will beat OM on March 15, 2026 in Ligue 1"
                                    className="min-h-[120px] bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#00FF66]/50 resize-none"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    disabled={isPending || isConfirming}
                                    data-testid="bet-description-input"
                                />
                                <p className="text-xs text-white/40">
                                    {formData.description.length}/500 characters
                                </p>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="category" className="text-white/80">Category</Label>
                                <Select
                                    value={formData.category}
                                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                                    disabled={isPending || isConfirming}
                                >
                                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="category-select">
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#1a0a30] border-white/10 z-50">
                                        {categories.map((cat) => (
                                            <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-white/10 cursor-pointer">
                                                {cat.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Stakes & Odds */}
                    <Card className="bg-white/[0.02] border-white/5">
                        <CardHeader>
                            <CardTitle className="text-xl text-white flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-[#FFD700]/20 flex items-center justify-center">
                                    <TrendingUp className="w-4 h-4 text-[#FFD700]" />
                                </div>
                                Stakes & Odds
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Currency selector — Plan A (B4P=2% + ×2 rewards, USDC/ETH=3%) */}
                            <div className="space-y-2" data-testid="currency-selector-block">
                                <Label className="text-white/80">{t('currency.label')}</Label>
                                <div className="grid grid-cols-3 gap-2">
                                    {currencies.map(c => {
                                        const active = formData.currency === c.code;
                                        return (
                                            <button
                                                type="button"
                                                key={c.code}
                                                onClick={() => setFormData({ ...formData, currency: c.code })}
                                                disabled={isPending || isConfirming}
                                                data-testid={`currency-btn-${c.code.toLowerCase()}`}
                                                className={`relative p-3 rounded-lg border-2 transition-all text-left ${active ? 'border-[#FFD800] bg-[#FFD800]/10 scale-[1.02]' : 'border-white/10 bg-white/5 hover:border-white/30'}`}
                                                style={active ? { boxShadow: `0 0 0 1px ${c.color}40, 0 4px 12px rgba(255,216,0,0.20)` } : {}}
                                            >
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="font-bold text-white" style={{ color: active ? '#FFD800' : c.color }}>{c.code}</span>
                                                    <span className="text-[10px] font-mono text-white/60">{t('currency.feeFormat', { pct: c.platform_fee_pct })}</span>
                                                </div>
                                                <div className="text-[11px] text-white/60 leading-tight">{c.badge}</div>
                                                {c.reward_multiplier > 1 && (
                                                    <div className="absolute -top-2 -right-2 bg-[#FFD800] text-[#003B71] text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md">
                                                        ×{c.reward_multiplier} {t('currency.rewardsBadge')}
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                                {selectedCurrency.code === 'B4P' && (
                                    <p className="text-xs text-[#FFD800] flex items-center gap-1.5" data-testid="b4p-perk-hint">
                                        <Coins className="w-3 h-3" />
                                        {t('currency.b4pPerk')}
                                    </p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="stake" className="text-white/80">{t('createBet.yourStake')} ({selectedCurrency.code})</Label>
                                <div className="relative">
                                    <Input
                                        id="stake"
                                        type="number"
                                        step={selectedCurrency.code === 'ETH' ? '0.001' : '1'}
                                        min={selectedCurrency.min_stake || 0.0001}
                                        placeholder={selectedCurrency.code === 'ETH' ? '0.001' : selectedCurrency.code === 'USDC' ? '10' : '500'}
                                        className="bg-white/5 border-white/10 text-white pr-20 font-mono placeholder:text-white/30 focus:border-[#00FF66]/50"
                                        value={formData.stake}
                                        onChange={(e) => setFormData({ ...formData, stake: e.target.value })}
                                        disabled={isPending || isConfirming}
                                        data-testid="stake-input"
                                    />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 font-mono">
                                        {selectedCurrency.code}
                                    </span>
                                </div>
                                {isConnected && selectedCurrency.code === 'ETH' && (
                                    <p className="text-xs text-white/40">
                                        Your balance: {balance.toFixed(4)} ETH
                                        {!hasEnoughBalance && formData.stake && (
                                            <span className="text-red-400 ml-2">
                                                (Insufficient)
                                            </span>
                                        )}
                                    </p>
                                )}
                                {selectedCurrency.code !== 'ETH' && (
                                    <p className="text-xs text-white/40" data-testid="non-eth-balance-hint">
                                        Min {selectedCurrency.min_stake} {selectedCurrency.code} · Max {selectedCurrency.max_stake.toLocaleString()} {selectedCurrency.code}
                                    </p>
                                )}
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <Label className="text-white/80">Odds</Label>
                                    <span className="font-mono text-2xl font-bold text-[#00FF66]">
                                        {oddsDisplay}x
                                    </span>
                                </div>
                                <Slider
                                    value={[formData.oddsBps]}
                                    onValueChange={([value]) => setFormData({ ...formData, oddsBps: value })}
                                    min={11000}
                                    max={50000}
                                    step={500}
                                    disabled={isPending || isConfirming}
                                    className="[&_[role=slider]]:bg-[#00FF66] [&_[role=slider]]:border-[#00FF66]"
                                    data-testid="odds-slider"
                                />
                                <div className="flex justify-between text-xs text-white/40">
                                    <span>1.10x (Low Risk)</span>
                                    <span>5.00x (High Risk)</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Resolution Time */}
                    <Card className="bg-white/[0.02] border-white/5">
                        <CardHeader>
                            <CardTitle className="text-xl text-white flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-[#6F3FCA]/20 flex items-center justify-center">
                                    <CalendarIcon className="w-4 h-4 text-[#9B6FE8]" />
                                </div>
                                Resolution Time
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        disabled={isPending || isConfirming}
                                        className={cn(
                                            "w-full justify-start text-left font-normal bg-white/5 border-white/10 text-white hover:bg-white/10",
                                            !formData.resolutionDate && "text-white/40"
                                        )}
                                        data-testid="resolution-date-picker"
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {formData.resolutionDate ? (
                                            format(formData.resolutionDate, "PPP 'at' HH:mm")
                                        ) : (
                                            <span>Pick a date</span>
                                        )}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0 bg-[#1a0a30] border-white/10" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={formData.resolutionDate}
                                        onSelect={(date) => date && setFormData({ ...formData, resolutionDate: addHours(date, 12) })}
                                        disabled={(date) => date < new Date()}
                                        initialFocus
                                        className="text-white"
                                    />
                                </PopoverContent>
                            </Popover>

                            <div className="flex gap-2 mt-4">
                                {[
                                    { label: '24h', days: 1 },
                                    { label: '3 days', days: 3 },
                                    { label: '1 week', days: 7 },
                                    { label: '1 month', days: 30 },
                                ].map((option) => (
                                    <Button
                                        key={option.label}
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        disabled={isPending || isConfirming}
                                        className="border-white/10 text-white/60 hover:bg-white/5 hover:text-white text-xs"
                                        onClick={() => setFormData({ ...formData, resolutionDate: addDays(new Date(), option.days) })}
                                    >
                                        {option.label}
                                    </Button>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Summary Sidebar */}
                <div className="space-y-6">
                    <CreateBetSummary
                        hash={hash}
                        formData={formData}
                        selectedCurrency={selectedCurrency}
                        oddsDisplay={oddsDisplay}
                        takerStake={takerStake}
                        totalPool={totalPool}
                        isPending={isPending}
                        isConfirming={isConfirming}
                        isConnected={isConnected}
                        hasEnoughBalance={hasEnoughBalance}
                        buttonContent={getButtonContent()}
                    />
                </div>
            </div>
        </form>
    );
}
